import {
  resolveAudioSource,
  resolveAudioSourceSyncIfPossible,
} from "./resolveSource";
import type { PlayerSliceDeps } from "../core/deps";
import type { DataSession } from "../../../data/core/session";
import type { AudioTrack } from "./AudioPlayer";
import { validateArtworkUrl } from "./validateArtwork";
import { isOfflineAccessInvalidError } from "@utils/offlineAccess/errors";

type PrepareTrackOptions = {
  forceAsyncSourceResolution?: boolean;
  forceRefreshCDNKey?: boolean;
  sourceRecoveryAttempt?: number;
};

/**
 * Prepares a track for playback by resolving its URL and fetching metadata.
 * Validates local artwork files (remote URLs are trusted and handled by AudioPlayer retry).
 * Throws if URL resolution fails.
 */
export async function prepareTrack(
  song: any,
  options: PrepareTrackOptions = {},
  catalog: Pick<
    PlayerSliceDeps["catalog"],
    "fetchFreshSongById" | "fetchSongById"
  >,
  data: DataSession,
): Promise<AudioTrack> {
  if (!song) {
    throw new Error("No song provided to prepareTrack");
  }

  console.audio.log("prepareTrack: resolving source for", song.id);

  // Try fully synchronous resolution for audio URL (avoids await-resume gaps on swipe path)
  const syncSource = options.forceAsyncSourceResolution
    ? null
    : resolveAudioSourceSyncIfPossible(data, { urlOnline: song.urlOnline });
  if (syncSource?.resolvedUrl) {
    console.audio.log("prepareTrack: sync audio path succeeded", {
      songId: song.id,
      isOffline: syncSource.isOffline,
    });
    // Even though audio resolved sync, validate artwork async (with HEAD request for remote URLs)
    const artwork = await validateArtworkUrl(song.albums?.[0]?.image, song.id);
    return buildTrack(song, syncSource.resolvedUrl, artwork, {
      canonicalUrl: syncSource.canonicalUrl,
      sourceType: syncSource.sourceType,
      sourceRecoveryAttempt: options.sourceRecoveryAttempt,
      cdnKeyRefreshRequested: syncSource.cdnKeyRefreshRequested,
    });
  }

  console.audio.log("prepareTrack: falling back to async path", {
    songId: song.id,
  });
  // Async path - handle missing local files
  try {
    const source = await resolveAudioSource(
      data,
      options.forceRefreshCDNKey
        ? { urlOnline: song.urlOnline, forceRefreshCDNKey: true }
        : { urlOnline: song.urlOnline },
    );
    // Async path: full artwork validation with HEAD request for remote URLs
    const artwork = await validateArtworkUrl(song.albums?.[0]?.image, song.id);
    return buildTrack(song, source.resolvedUrl, artwork, {
      canonicalUrl: source.canonicalUrl,
      sourceType: source.sourceType,
      sourceRecoveryAttempt: options.sourceRecoveryAttempt,
      cdnKeyRefreshRequested: source.cdnKeyRefreshRequested,
    });
  } catch (error: any) {
    const missingLocalFile = error?.message?.startsWith("MISSING_LOCAL_FILE:");
    const offlineAccessInvalid = isOfflineAccessInvalidError(error);
    if (missingLocalFile || offlineAccessInvalid) {
      console.audio.log("Fetching fresh song data for", song.id);

      // Invalid access must bypass local substitution so an online session can
      // fall back to normal streaming. Missing files retain the existing
      // cache-aware repair path.
      const freshSong = offlineAccessInvalid
        ? await catalog.fetchFreshSongById(song.id)
        : await catalog.fetchSongById(song.id);
      if (!freshSong) {
        throw new Error(`Song ${song.id} not found in catalog`);
      }

      // resolveAudioSource will check file.exists for offline URLs
      // and throw MISSING_LOCAL_FILE if the file is truly gone
      let freshSource;
      try {
        freshSource = await resolveAudioSource(
          data,
          options.forceRefreshCDNKey
            ? { urlOnline: freshSong.urlOnline, forceRefreshCDNKey: true }
            : { urlOnline: freshSong.urlOnline },
        );
      } catch (retryError: any) {
        if (
          retryError?.message?.startsWith("MISSING_LOCAL_FILE:") ||
          isOfflineAccessInvalidError(retryError)
        ) {
          throw new Error(
            `Song ${song.id} offline file missing and no online URL available`,
          );
        }
        throw retryError;
      }
      // Async path: full artwork validation with HEAD request
      const artwork = await validateArtworkUrl(
        freshSong.albums?.[0]?.image,
        freshSong.id,
      );
      return buildTrack(freshSong, freshSource.resolvedUrl, artwork, {
        canonicalUrl: freshSource.canonicalUrl,
        sourceType: freshSource.sourceType,
        sourceRecoveryAttempt: options.sourceRecoveryAttempt,
        cdnKeyRefreshRequested: freshSource.cdnKeyRefreshRequested,
      });
    }
    throw error;
  }
}

function buildTrack(
  song: any,
  url: string,
  artwork: string,
  source: Pick<
    AudioTrack,
    | "canonicalUrl"
    | "sourceType"
    | "sourceRecoveryAttempt"
    | "cdnKeyRefreshRequested"
  >,
): AudioTrack {
  const artist = song.artists
    .map((a: any) => a?.name)
    .filter(Boolean)
    .join(", ");

  const track: AudioTrack = {
    id: song.id,
    url,
    canonicalUrl: source.canonicalUrl,
    title: song.title,
    artist,
    artwork: artwork || undefined,
  };
  if (song.duration > 0) track.durationMs = song.duration * 1000;
  if (source.sourceType) track.sourceType = source.sourceType;
  if (source.sourceRecoveryAttempt != null) {
    track.sourceRecoveryAttempt = source.sourceRecoveryAttempt;
  }
  if (source.cdnKeyRefreshRequested != null) {
    track.cdnKeyRefreshRequested = source.cdnKeyRefreshRequested;
  }
  return track;
}
